import React, { useState, useRef, useEffect, useCallback } from 'react';
import QrScanner from 'react-qr-scanner';
import { X, Camera, AlertCircle } from 'lucide-react';
import Button from '../common/Button';

const QRScannerModal = ({ isOpen, onClose, onScan, loading }) => {
  const [error, setError] = useState(null);
  const [facingMode, setFacingMode] = useState('environment');
  const [isScanning, setIsScanning] = useState(true);
  const [lastScannedCode, setLastScannedCode] = useState(null);
  const [scanCooldown, setScanCooldown] = useState(false);
  const scannerRef = useRef(null);

  // Reset scanner state when modal opens
  useEffect(() => {
    if (isOpen) {
      setError(null);
      setLastScannedCode(null);
      setScanCooldown(false);
      setIsScanning(true);
    }
  }, [isOpen]);

  // Move useCallback before early return to follow Rules of Hooks
  const handleScan = useCallback((data) => {
    if (data && data.text && !scanCooldown && isScanning) {
      const qrCode = data.text;
      
      // Prevent duplicate scans of the same code
      if (qrCode === lastScannedCode) {
        return;
      }
      
      console.log('QR Code scanned:', qrCode);
      setLastScannedCode(qrCode);
      setScanCooldown(true);
      
      // Call the parent scan handler
      onScan(qrCode);
      
      // Reset after a short delay to allow continuous scanning
      setTimeout(() => {
        setLastScannedCode(null);
        setScanCooldown(false);
        console.log('Scanner ready for next scan');
      }, 1500); // 1.5 second cooldown
    }
  }, [scanCooldown, isScanning, lastScannedCode, onScan]);

  const handleError = (err) => {
    console.error('QR Scanner error:', err);
    setError('Camera access denied or not available. Please check your browser permissions.');
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
    // Reset scanning state when switching cameras
    setLastScannedCode(null);
    setScanCooldown(false);
  };

  const previewStyle = {
    height: 'auto',
    minHeight: 250,
    maxHeight: 400,
    width: '100%',
  };

  // Early return after all hooks to follow Rules of Hooks
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="relative bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-2xl">
          {/* Header */}
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex items-center gap-2 sm:gap-3">
              <Camera className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600" />
              <h3 className="text-lg sm:text-xl font-semibold text-gray-900">
                QR Code Scanner
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors p-1"
              disabled={loading}
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Scanner */}
          <div className="px-3 sm:px-6 py-4 sm:py-6">
            {error ? (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-sm font-medium text-red-800 mb-1">Camera Error</h4>
                  <p className="text-sm text-red-700">{error}</p>
                  <p className="text-xs text-red-600 mt-2">
                    Make sure you've granted camera permissions in your browser settings.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="relative bg-black rounded-lg overflow-hidden">
                  <QrScanner
                    ref={scannerRef}
                    delay={100}
                    style={previewStyle}
                    onError={handleError}
                    onScan={handleScan}
                    constraints={{
                      video: { facingMode: facingMode }
                    }}
                  />
                  {(loading || scanCooldown) && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                      <div className="text-white text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-2"></div>
                        <p>{loading ? 'Processing...' : 'Ready for next scan...'}</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 text-center">
                    📱 Position the QR code within the camera frame
                  </p>
                  <p className="text-xs text-blue-600 text-center mt-1">
                    Ready for multiple scans - no need to close
                  </p>
                </div>

                <div className="flex justify-center">
                  <Button
                    variant="secondary"
                    onClick={toggleCamera}
                    className="gap-2 w-full sm:w-auto"
                    disabled={loading}
                  >
                    <Camera className="h-4 w-4" />
                    <span className="hidden xs:inline">Switch Camera</span>
                    <span className="xs:hidden">Switch</span>
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={loading}
              className="w-full sm:w-auto"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRScannerModal;
